/**
 * StatusBadge Component
 * Color-coded status display for order statuses
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-007
 */

import type { OrderStatus } from '@/types/order';
import { Badge } from '@/components/ui/badge';

// ============================================
// STATUS CONFIGURATION
// ============================================

/**
 * Status badge color configuration
 * Maps status to Tailwind colors and labels
 */
const STATUS_CONFIG: Record<OrderStatus, {
  label: string;
  colorClass: string;
}> = {
  ORDER_INITIALIZED: {
    label: 'Order Initialized',
    colorClass: 'bg-slate-100 text-slate-800 border-slate-200 hover:bg-slate-200',
  },
  PENDING: {
    label: 'Pending',
    colorClass: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200',
  },
  CONFIRMED: {
    label: 'Confirmed',
    colorClass: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    colorClass: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200',
  },
  READY: {
    label: 'Ready',
    colorClass: 'bg-cyan-100 text-cyan-800 border-cyan-200 hover:bg-cyan-200',
  },
  IN_TRANSIT: {
    label: 'In Transit',
    colorClass: 'bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-200',
  },
  DELIVERED: {
    label: 'Delivered',
    colorClass: 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200',
  },
  COMPLETED: {
    label: 'Completed',
    colorClass: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
  },
  CANCELLED: {
    label: 'Cancelled',
    colorClass: 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200',
  },
};

// ============================================
// COMPONENT PROPS
// ============================================

export interface StatusBadgeProps {
  status: OrderStatus;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

/**
 * StatusBadge Component
 * Displays order status with color-coded badge
 *
 * @example
 * ```tsx
 * <StatusBadge status="DISPATCHED" size="md" />
 * ```
 */
export function StatusBadge({ status, size = 'md', className = '' }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5',
  };

  return (
    <Badge
      variant="outline"
      className={`${config.colorClass} ${sizeClasses[size]} ${className} font-medium`}
    >
      {config.label}
    </Badge>
  );
}

export default StatusBadge;
